import { useMemo, useState } from "react";
import { fmt } from "../lib/format.js";
import { Button, StatusPill } from "./primitives.jsx";
import { DateRangePicker, Input, Select } from "./ui/index.js";
import { EmptyResult } from "./charts.jsx";

const NUMERIC = new Set(["int", "dec", "pct", "duration"]);

/** A row's searchable text: every column's raw value, its displayed form and its second line. */
const rowText = (r, columns) =>
  columns
    .map((c) => `${r[c.key] ?? ""} ${c.format && c.format !== "status" && c.format !== "action" ? fmt(r[c.key], c.format) : ""} ${c.sub ? (r[c.sub] ?? "") : ""}`)
    .join(" ")
    .toLowerCase();

/** One cell, by column kind: status pill, action button, two-line text, or a formatted value. */
function Cell({ c, r, onRowClick }) {
  const v = r[c.key];
  if (c.format === "status") return v ? <StatusPill tone={c.tones?.[v] ?? "neutral"}>{v}</StatusPill> : null;
  if (c.format === "action") {
    if (!v) return null;
    return onRowClick ? (
      <span onClick={(e) => e.stopPropagation()}>
        <Button small onClick={() => onRowClick(r)}>
          {v}
        </Button>
      </span>
    ) : (
      <span className="text-ink-2">{v}</span>
    );
  }
  const text = c.format && c.format !== "text" ? fmt(v, c.format) : (v ?? "");
  if (!c.sub) return text;
  return (
    <>
      <span className="block font-medium text-ink">{text}</span>
      {r[c.sub] && <span className="mt-0.5 block text-[11px] text-ink-3">{r[c.sub]}</span>}
    </>
  );
}

/** Rows as cards below the sm breakpoint, 30 at a time. */
function PhoneCards({ columns, rows, onRowClick }) {
  const [n, setN] = useState(30);
  // A leading ID column sits above the title instead of heading the card.
  const idCol = columns[0]?.id && columns.length > 1 ? columns[0] : null;
  const [head, ...rest] = idCol ? columns.slice(1) : columns;
  const pills = rest.filter((c) => c.format === "status");
  const action = rest.find((c) => c.format === "action");
  const fields = rest.filter((c) => c.format !== "status" && c.format !== "action");
  return (
    <div className="-mx-5 border-t border-line sm:hidden">
      <ul>
        {rows.slice(0, n).map((r, i) => (
          <li key={i} className="border-b border-line px-5 py-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 text-[13px]">
                {idCol && r[idCol.key] != null && r[idCol.key] !== "" && <span className="tabular block text-[11px] text-ink-3">{idCol.label} {r[idCol.key]}</span>}
                <Cell c={head} r={r} onRowClick={onRowClick} />
              </div>
            </div>
            {pills.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {pills.map((c) => (
                  <Cell key={c.key} c={c} r={r} />
                ))}
              </div>
            )}
            {fields.length > 0 && (
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                {fields.map((c) => (
                  <div key={c.key} className="min-w-0">
                    <dt className="truncate text-[11px] text-ink-3">{c.label}</dt>
                    <dd className="tabular truncate text-ink">{c.format && c.format !== "text" ? fmt(r[c.key], c.format) : (r[c.key] ?? "")}</dd>
                  </div>
                ))}
              </dl>
            )}
            {action && r[action.key] && onRowClick && (
              <button type="button" onClick={() => onRowClick(r)} className="mt-2.5 min-h-11 w-full rounded-control border border-line-default text-[12.5px] font-medium text-ink">
                {r[action.key]}
              </button>
            )}
            {!action && onRowClick && (
              <button type="button" onClick={() => onRowClick(r)} className="mt-2 min-h-11 text-[12px] text-accent-ink underline underline-offset-2">
                Open details
              </button>
            )}
          </li>
        ))}
      </ul>
      {rows.length > n && (
        <button type="button" onClick={() => setN((x) => x + 30)} className="min-h-11 w-full text-[12.5px] font-medium text-accent-ink">
          Show 30 more of {fmt(rows.length - n, "int")}
        </button>
      )}
    </div>
  );
}

/**
 * Table with client-side sorting and search (or server-side when `onSort` is
 * given: the caller then owns sorting and its own search box).
 * `columns`: [{ key, label, format?, align?, wrap?, sub?, tones?, hidden?, id? }]
 *   format "status": the value as a tinted pill, coloured by `tones[value]`
 *   format "action": the value as a button that opens the row (needs `onRowClick`)
 *   sub:    key of a second, muted line under the value (e.g. "ID · format")
 *   hidden: kept for search and CSV, not drawn
 *   id:     the row's ID; as the first column, phone cards show it above the title instead of as the heading
 * `tabs`: { key, all, order? } — underline tabs that filter rows by that column, with counts.
 * `filters`: { select?: { key, label, all? }, dates?: { key, label } } — beside the search box,
 *   a dropdown of that column's values and a from/to range on a "YYYY-MM-DD" column.
 * `onRowClick(row)` makes rows selectable; `isSelected(row)` highlights the current one.
 */
export function DataTable({ columns, rows, maxHeight = 460, sort: controlledSort, onSort, dense = false, onRowClick, isSelected, tabs, filters, onExportView }) {
  const [localSort, setLocalSort] = useState(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("");
  const [pick, setPick] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const sort = controlledSort ?? localSort;
  const searchable = !onSort;
  // A short table needs no search box or row count.
  const compact = !tabs && rows.length <= 10;
  const shown = columns.filter((c) => !c.hidden);

  const tabList = useMemo(() => {
    if (!tabs) return null;
    const counts = new Map();
    for (const r of rows) counts.set(r[tabs.key], (counts.get(r[tabs.key]) ?? 0) + 1);
    const order = tabs.order ?? [...counts.keys()];
    return [["", tabs.all ?? "All", rows.length], ...order.filter((v) => counts.has(v)).map((v) => [v, v, counts.get(v)])];
  }, [tabs, rows]);

  const pickOptions = useMemo(() => {
    const key = filters?.select?.key;
    if (!key) return [];
    return [...new Set(rows.map((r) => r[key]).filter((v) => v !== null && v !== undefined && v !== ""))].sort((a, b) => String(a).localeCompare(String(b)));
  }, [rows, filters]);

  // The calendar only offers days that some row falls on or between.
  const dateBounds = useMemo(() => {
    const key = filters?.dates?.key;
    if (!key) return {};
    const days = rows.map((r) => String(r[key] ?? "").slice(0, 10)).filter(Boolean).sort();
    return { min: days[0], max: days[days.length - 1] };
  }, [rows, filters]);

  const inTab = useMemo(() => (tabs && tab ? rows.filter((r) => r[tabs.key] === tab) : rows), [rows, tabs, tab]);

  const filtered = useMemo(() => {
    const sel = filters?.select?.key;
    const day = filters?.dates?.key;
    if (!(sel && pick) && !(day && (from || to))) return inTab;
    return inTab.filter((r) => {
      if (sel && pick && r[sel] !== pick) return false;
      if (day && (from || to)) {
        // ISO dates compare as strings; rows without a date drop out of a range.
        const d = String(r[day] ?? "").slice(0, 10);
        if (!d || (from && d < from) || (to && d > to)) return false;
      }
      return true;
    });
  }, [inTab, filters, pick, from, to]);
  const filtering = Boolean(pick || from || to);

  const matched = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!searchable || !terms.length) return filtered;
    // Every word must appear somewhere in the row, in any column.
    return filtered.filter((r) => {
      const text = rowText(r, columns);
      return terms.every((t) => text.includes(t));
    });
  }, [filtered, columns, query, searchable]);

  const sorted = useMemo(() => {
    if (onSort || !sort) return matched;
    const col = columns.find((c) => c.key === sort.key);
    const numeric = NUMERIC.has(col?.format);
    return [...matched].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      // Missing values ("—") sort last either way.
      if (av === null || av === undefined || av === "") return 1;
      if (bv === null || bv === undefined || bv === "") return -1;
      const cmp = numeric ? Number(av) - Number(bv) : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [matched, sort, columns, onSort]);

  const toggle = (key) => {
    const next = sort?.key === key ? { key, dir: sort.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" };
    if (onSort) onSort(next);
    else setLocalSort(next);
  };

  if (!rows.length) return <EmptyResult />;

  return (
    <div>
      {tabList && (
        <div className="-mt-1 mb-3 flex flex-wrap gap-x-5 border-b border-line" role="tablist" aria-label="Filter rows">
          {tabList.map(([v, label, n]) => (
            <button
              key={v || "all"}
              type="button"
              role="tab"
              aria-selected={tab === v}
              onClick={() => setTab(v)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-0.5 pb-2 text-[12px] transition-colors ${tab === v ? "border-accent font-semibold text-accent-ink" : "border-transparent text-ink-2 hover:text-ink"}`}
            >
              {label} <span className={`tabular rounded-full px-1.5 text-[10.5px] ${tab === v ? "bg-accent-soft" : "bg-surface-2 text-ink-3"}`}>{fmt(n, "int")}</span>
            </button>
          ))}
        </div>
      )}
      {searchable && !compact && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="block w-72 max-w-full">
            <span className="sr-only">Search rows</span>
            <Input size="sm" icon="search" type="search" placeholder={tabs ? "Search every row" : "Search rows…"} value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          {filters?.select && pickOptions.length > 0 && (
            <label className="block w-44 max-w-full">
              <span className="sr-only">{filters.select.label}</span>
              <Select size="sm" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">{filters.select.all ?? `All ${filters.select.label.toLowerCase()}s`}</option>
                {pickOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Select>
            </label>
          )}
          {filters?.dates && (
            <DateRangePicker
              label={filters.dates.label}
              placeholder={`${filters.dates.label}: any date`}
              className="w-60 max-w-full"
              value={{ from, to }}
              min={dateBounds.min}
              max={dateBounds.max}
              onChange={(r) => {
                setFrom(r.from);
                setTo(r.to);
              }}
            />
          )}
          {filtering && (
            <button
              type="button"
              className="text-[12px] text-accent-ink underline underline-offset-2"
              onClick={() => {
                setPick("");
                setFrom("");
                setTo("");
              }}
            >
              Clear filters
            </button>
          )}
          {tabs && tab && <StatusPill>Showing: {tab}</StatusPill>}
          {/* Exactly the rows on screen (tab + filters + search), with the card's scope (mockup "Export this view"). */}
          {onExportView && (tab || filtering || query.trim()) && (
            <Button
              size="xs"
              icon="download"
              className="ml-auto"
              onClick={() =>
                onExportView(sorted, {
                  tab,
                  query: query.trim(),
                  filters: [pick && `${filters.select.label.toLowerCase()} "${pick}"`, (from || to) && `${filters.dates.label.toLowerCase()} ${from || "…"} to ${to || "…"}`].filter(Boolean).join(", "),
                })
              }
            >
              Export this view ({fmt(sorted.length, "int")})
            </Button>
          )}
        </div>
      )}
      {!sorted.length ? (
        <div className="flex h-24 items-center justify-center text-[12px] text-ink-3">
          {query.trim() ? `No rows match “${query.trim()}”.` : filtering ? "No rows match these filters." : "No rows in this tab."}
        </div>
      ) : (
        <>
        {/* Phones: each row is a decision card (title, status, action; the rest on two columns). */}
        <PhoneCards columns={shown} rows={sorted} onRowClick={onRowClick} />
        <div className="scroll-thin hidden overflow-auto rounded-control border border-line sm:block" style={{ maxHeight }}>
          <table className="w-full border-collapse text-[12.5px]">
            <thead className="sticky top-0 z-[1] bg-surface-2">
              <tr>
                {shown.map((c, i) => {
                  const right = c.align === "right" || NUMERIC.has(c.format);
                  const active = sort?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                      className={`border-b border-line py-3 text-[12px] font-medium text-ink-2 ${i === 0 ? "pl-4" : "pl-3"} ${i === shown.length - 1 ? "pr-4" : "pr-3"} ${right ? "text-right" : "text-left"}`}
                    >
                      {c.format === "action" ? (
                        c.label
                      ) : (
                        // Right-aligned: the arrow goes before the label, so the label ends flush with the values below.
                        <button type="button" className={`inline-flex items-center gap-1 hover:text-ink ${right ? "flex-row-reverse text-right" : "text-left"}`} onClick={() => toggle(c.key)}>
                          {c.label}
                          <span aria-hidden className={active ? "text-ink" : "text-transparent"}>
                            {active && sort.dir === "asc" ? "↑" : "↓"}
                          </span>
                        </button>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, ri) => (
                <tr
                  key={ri}
                  className={`border-b border-line last:border-0 hover:bg-surface-2 ${onRowClick ? "cursor-pointer" : ""} ${isSelected?.(r) ? "bg-accent-soft" : ""}`}
                  style={isSelected?.(r) ? { boxShadow: "inset 3px 0 0 var(--accent)" } : undefined}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onRowClick(r)) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  aria-selected={onRowClick ? Boolean(isSelected?.(r)) : undefined}
                >
                  {shown.map((c, i) => {
                    const right = c.align === "right" || NUMERIC.has(c.format);
                    return (
                      <td
                        key={c.key}
                        className={`${dense ? "py-2" : "py-3"} ${i === 0 ? "pl-4" : "pl-3"} ${i === shown.length - 1 ? "pr-4" : "pr-3"} align-middle ${right ? "tabular text-right" : "text-left"} ${c.wrap || c.sub ? "min-w-56" : "whitespace-nowrap"} text-ink`}
                      >
                        <Cell c={c} r={r} onRowClick={onRowClick} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      {searchable && !compact && sorted.length > 0 && (
        <p className="tabular pt-3 text-[11.5px] text-ink-3" aria-live="polite">
          Showing {fmt(sorted.length, "int")} of {fmt(rows.length, "int")} rows
        </p>
      )}
    </div>
  );
}
